import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  useApproveNotification,
  useCompleteHitl,
  useRejectNotification,
  useSkipHitl,
} from "@/hooks/useNotifications";

interface StepActionBarProps {
  /** The inbox notification this step's open checkpoint/HITL action resolves
   *  to — see `AgentRunStepDto.approval_notification_id`. */
  notificationId: string;
  /** A HITL step renders Mark done / Skip; a checkpoint renders Approve / Reject. */
  isHitl: boolean;
}

/**
 * Inline approve/reject (checkpoint) or mark-done/skip (HITL) for a run step,
 * posting against the step's own inbox notification. One approval path shared
 * with the Inbox tab (`InboxRow`) — same hooks, same buttons.
 */
export function StepActionBar({ notificationId, isHitl }: StepActionBarProps) {
  const approve = useApproveNotification();
  const reject = useRejectNotification();
  const completeHitl = useCompleteHitl();
  const skipHitl = useSkipHitl();

  const busy =
    approve.isPending ||
    reject.isPending ||
    completeHitl.isPending ||
    skipHitl.isPending;

  const onPrimary = () =>
    (isHitl ? completeHitl : approve).mutate(notificationId, {
      onError: () =>
        toast.error(isHitl ? "Could not mark done." : "Could not approve."),
    });
  const onSecondary = () =>
    (isHitl ? skipHitl : reject).mutate(notificationId, {
      onError: () => toast.error(isHitl ? "Could not skip." : "Could not reject."),
    });

  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button size="sm" variant="default" disabled={busy} onClick={onPrimary}>
        {isHitl ? "Mark done" : "Approve"}
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={onSecondary}>
        {isHitl ? "Skip" : "Reject"}
      </Button>
    </div>
  );
}
